/**
 * Aviso de contato repetido: quando o backend barra a criação de um contato
 * porque o CPF/CNPJ (ou a raiz do CNPJ, matriz/filial) já está na base, a
 * resposta 409 traz `conflict` com o contato e os cards que já ocupam o
 * documento. Qualquer tela de cadastro chama `openDuplicateContact(erro)` no
 * `catch`; o `GlobalDuplicateContact` (montado em `routes/Container.jsx`) abre
 * a mini modal apontando para eles.
 */
import axios from "../config/axiosInstance";
import { LEADS } from "../config/apiConfig";

export const DUPLICATE_CONTACT_EVENT = "relinq:duplicate-contact";

/** `{ conflict, message }` quando o erro é um 409 de contato repetido; senão null. */
export const getDuplicateConflict = (error) => {
  const response = error?.response;
  if (Number(response?.status) !== 409) return null;
  const conflict = response?.data?.conflict;
  if (!conflict?.lead?.id) return null;
  return { conflict, message: response?.data?.message || "" };
};

/**
 * Abre a modal de contato repetido se o erro for desse tipo. Devolve `true`
 * quando abriu (a tela não precisa mostrar outro aviso). `onOpen` roda quando a
 * pessoa escolhe abrir o contato ou um card, antes de navegar (ex.: fechar o
 * formulário de cadastro que ficou aberto).
 */
export const openDuplicateContact = (error, { onOpen } = {}) => {
  const info = getDuplicateConflict(error);
  if (!info || typeof window === "undefined") return false;
  window.dispatchEvent(
    new CustomEvent(DUPLICATE_CONTACT_EVENT, { detail: { ...info, onOpen } }),
  );
  return true;
};

/**
 * Mesma modal a partir do id de um contato que já existia (linha "já existia"
 * do resultado de uma importação): busca o contato e os cards que o usuário
 * pode abrir. Devolve `true` se abriu.
 */
export const openDuplicateContactById = async (leadId, { message, onOpen } = {}) => {
  if (!leadId || typeof window === "undefined") return false;
  try {
    const { data } = await axios.get(LEADS.DUPLICATE_PREVIEW(leadId));
    const conflict = data?.data;
    if (!conflict?.lead?.id) return false;
    window.dispatchEvent(
      new CustomEvent(DUPLICATE_CONTACT_EVENT, {
        detail: { conflict, message: message || "", onOpen },
      }),
    );
    return true;
  } catch {
    return false;
  }
};

/** Caminhos para abrir o contato e o card apontados pelo aviso. */
export const duplicateContactPath = (leadId) =>
  `/contacts?leadId=${encodeURIComponent(leadId)}`;

export const duplicateCardPath = (card) =>
  card?.funnelId
    ? `/crm?f=${encodeURIComponent(card.funnelId)}&cardId=${encodeURIComponent(card.id)}`
    : `/crm?cardId=${encodeURIComponent(card.id)}`;
